import bindAll from 'lodash.bindall';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import Modal from '../../containers/modal.jsx';
import {HashPrefix} from '../../lib/hash-prefix';
import {defaultProjectId} from '../../reducers/project-state';
import {http} from '../../lib/http/request';
import {error, success} from '../../lib/toast';

import styles from './share-modal.css';
import Switch from '../switch/switch';

const SHARE_SCOPE_ALL = 'all';
const SHARE_SCOPE_ACCOUNT = 'account';

const getCurrentProjectId = projectId => {
    const normalizedProjectId = String(projectId || '').trim();
    if (!normalizedProjectId || normalizedProjectId === defaultProjectId) return '';
    return normalizedProjectId;
};

const buildShareUrl = shareId => {
    if (!shareId) return '';
    return `${window.location.origin}${window.location.pathname}${window.location.search}` +
        `#${HashPrefix.SHARE}${shareId}`;
};

class ShareModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleAccountChange',
            'handleCopyLink',
            'handleExpiryChange',
            'handleScopeChange',
            'handleSubmit',
            'handleRemixChange',
            'handleEditChange',
            'handleCopyChange'
        ]);
        this.state = {
            account: '',
            canCopy: true,
            canRemix: true,
            canEdit: true,
            copied: false,
            expiresAt: '',
            scope: SHARE_SCOPE_ALL,
            submitted: false,
            shareCode: null
        };
        this.copyTimer = null;
    }
    componentWillUnmount () {
        if (this.copyTimer) {
            clearTimeout(this.copyTimer);
        }
    }
    handleScopeChange (event) {
        this.setState({
            scope: event.currentTarget.value,
            submitted: false
        });
    }
    handleRemixChange (checked) {
        this.setState({
            canRemix: checked,
            submitted: false
        });
    }
    handleEditChange (checked) {
        this.setState({
            canEdit: checked,
            submitted: false
        });
    }
    handleCopyChange (checked) {
        this.setState({
            canCopy: checked,
            submitted: false
        });
    }
    handleAccountChange (event) {
        this.setState({
            account: event.currentTarget.value,
            submitted: false
        });
    }
    handleExpiryChange (event) {
        this.setState({
            expiresAt: event.currentTarget.value,
            submitted: false
        });
    }
    handleSubmit () {
        const currentProjectId = getCurrentProjectId(this.props.projectId);
        const account = this.state.account.trim();
        if (!currentProjectId || (this.state.scope === SHARE_SCOPE_ACCOUNT && !account)) return;
        const expiresAt = this.state.expiresAt ? new Date(this.state.expiresAt) : null;
        const data = {
            account,
            canCopy: this.state.canCopy,
            canRemix: this.state.canRemix,
            canEdit: this.state.canEdit,
            expiresAt,
            projectId: currentProjectId,
            scope: this.state.scope
        };
        http.post('/scratchProject/link', data).then(res => {
            if (res.success) {
                this.setState({
                    submitted: true,
                    shareCode: res.result
                });
                success(res.message);
                return;
            }
            this.setState({
                submitted: true,
                shareCode: null
            });
            error(res.message);
        });
    }
    handleCopyLink () {
        const link = buildShareUrl(this.state.shareCode);
        if (!link) return;
        const onCopied = () => {
            this.setState({copied: true});
            if (this.copyTimer) clearTimeout(this.copyTimer);
            this.copyTimer = setTimeout(() => {
                this.setState({copied: false});
            }, 1800);
        };
        const fallbackCopy = () => {
            const textarea = document.createElement('textarea');
            textarea.value = link;
            textarea.setAttribute('readonly', '');
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            try {
                if (document.execCommand('copy')) {
                    onCopied();
                }
            } finally {
                document.body.removeChild(textarea);
            }
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(link)
                .then(onCopied)
                .catch(fallbackCopy);
        } else {
            fallbackCopy();
        }
    }
    renderScopeOption (value, label, description) {
        return (
            <label
                className={classNames(styles.optionCard, {
                    [styles.optionCardActive]: this.state.scope === value
                })}
            >
                <input
                    checked={this.state.scope === value}
                    name="shareScope"
                    type="radio"
                    value={value}
                    onChange={this.handleScopeChange}
                />
                <span>
                    <strong>{label}</strong>
                    <span>{description}</span>
                </span>
            </label>
        );
    }
    renderToggle (checked, label, description, onChange, name) {
        return (
            <label className={styles.switchRow}>
                <Switch
                    checked={checked}
                    name={name}
                    onChange={onChange}
                />
                <span className={styles.switchText}>
                    <strong>{label}</strong>
                    <span className={styles.switchDescription}>{description}</span>
                </span>
            </label>
        );
    }
    render () {
        const currentProjectId = getCurrentProjectId(this.props.projectId);
        const shareUrl = buildShareUrl(this.state.shareCode);
        const needsAccount = this.state.scope === SHARE_SCOPE_ACCOUNT;
        const accountInvalid = needsAccount && !this.state.account.trim();
        const canApply = Boolean(currentProjectId) && !accountInvalid;
        const shareReady = this.state.shareCode !== null;

        return (
            <Modal
                className={styles.modalContent}
                contentLabel="分享作品"
                id="share-modal"
                onRequestClose={this.props.onRequestClose}
            >
                <div className={styles.shareModal}>
                    <div className={styles.formSection}>
                        <div className={styles.sectionTitle}>{'分享范围'}</div>
                        <div className={styles.optionGrid}>
                            {this.renderScopeOption(SHARE_SCOPE_ALL, '所有人', '拿到链接即可访问作品')}
                            {this.renderScopeOption(SHARE_SCOPE_ACCOUNT, '指定账号', '仅允许指定手机号或用户编号访问')}
                        </div>
                    </div>

                    {needsAccount ? (
                        <label className={styles.field}>
                            <span>{'手机号或用户编号'}</span>
                            <input
                                autoFocus
                                className={styles.textInput}
                                placeholder="请输入手机号或用户编号"
                                type="text"
                                value={this.state.account}
                                onChange={this.handleAccountChange}
                            />
                            {accountInvalid ? (
                                <span className={styles.fieldHint}>{'指定账号时必须填写访问账号。'}</span>
                            ) : null}
                        </label>
                    ) : null}

                    <div className={styles.formSection}>
                        <div className={styles.sectionTitle}>{'访问设置'}</div>
                        <div className={styles.switchGrid}>
                            {this.renderToggle(
                                this.state.canRemix,
                                '允许改编',
                                '别人可基于此作品继续创作',
                                this.handleRemixChange,
                                'shareCanRemix'
                            )}
                            {this.renderToggle(
                                this.state.canCopy,
                                '允许复制',
                                '别人可直接复制一份作品',
                                this.handleCopyChange,
                                'shareCanCopy'
                            )}
                            {this.renderToggle(
                                this.state.canEdit,
                                '允许编辑',
                                '别人可直接编辑作品',
                                this.handleEditChange,
                                'shareCanEdit'
                            )}
                        </div>
                    </div>

                    <label className={styles.field}>
                        <span>{'有效期'}</span>
                        <input
                            className={styles.textInput}
                            type="datetime-local"
                            value={this.state.expiresAt}
                            onChange={this.handleExpiryChange}
                        />
                    </label>

                    <div className={styles.linkPanel}>
                        <div className={styles.linkHeader}>
                            <span>{'分享链接'}</span>
                            {currentProjectId ? (
                                <span className={styles.linkStatus}>{'前端已生成'}</span>
                            ) : (
                                <span className={styles.linkWarning}>{'保存作品后生成链接'}</span>
                            )}
                        </div>
                        {this.state.submitted ?
                            <input
                                readOnly
                                className={styles.linkInput}
                                value={shareUrl || '当前作品还没有可分享的项目编号'}
                            /> :
                            null
                        }
                    </div>

                    {this.state.submitted ? (
                        <div className={styles.resultPanel}>
                            {shareReady ? (
                                <React.Fragment>
                                    <strong>{'分享设置已生成'}</strong>
                                    <span>
                                        {`范围：${needsAccount ? this.state.account.trim() : '所有人'}，改编：${
                                            this.state.canRemix ? '允许' : '禁止'
                                        }，复制：${this.state.canCopy ? '允许' : '禁止'}，有效期：${
                                            this.state.expiresAt || '长期有效'
                                        }，编辑：${this.state.canEdit ? '允许' : '禁止'}，有效期：${
                                            this.state.expiresAt || '长期有效'
                                        }`}
                                    </span>
                                </React.Fragment>
                            ) : (
                                <span>{accountInvalid ? '请先填写指定账号。' : '请先保存作品，再生成分享链接。'}</span>
                            )}
                        </div>
                    ) : null}

                    <div className={styles.buttonRow}>
                        <button
                            className={styles.secondaryButton}
                            type="button"
                            onClick={this.props.onRequestClose}
                        >
                            {'取消'}
                        </button>
                        {this.state.submitted ? (
                            <button
                                className={styles.secondaryButton}
                                disabled={!shareUrl}
                                type="button"
                                onClick={this.handleCopyLink}
                            >
                                {this.state.copied ? '已复制' : '复制链接'}
                            </button>
                        ) : null}

                        <button
                            className={styles.primaryButton}
                            disabled={!canApply}
                            type="button"
                            onClick={this.handleSubmit}
                        >
                            {'生成分享'}
                        </button>
                    </div>
                </div>
            </Modal>
        );
    }
}

ShareModal.propTypes = {
    onRequestClose: PropTypes.func,
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

ShareModal.defaultProps = {
    onRequestClose: () => {},
    projectId: null
};

export default ShareModal;
